import {useLocation, useNavigate} from "react-router-dom";
import {
  Alert,
  Anchor,
  Box,
  Center,
  CloseButton,
  Container,
  Group,
  LoadingOverlay,
  Paper,
  Space,
  Title
} from "@mantine/core";
import {
  adjustToTimeZone,
  getObjectId,
  SearchCriteria,
  useDownloadCompanyTransportServicesReport
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useEffect} from "react";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {
  CompanyTransportServicesReportForm,
  CompanyTransportServicesReportFormValues
} from "../../components/form/CompanyTransportServicesReportForm/CompanyTransportServicesReportForm";
import {IconAlertCircle} from "@tabler/icons-react";
import {useScrollIntoView} from "@mantine/hooks";

type Props = {
  formDefaults?: Partial<CompanyTransportServicesReportFormValues>
}

export const CompanyTransportServicesReportFormPageRoute = () => {
  const {state} = useLocation();

  const formDefaults = state?.formDefaults;

  return (
    <CompanyTransportServicesReportFormPage formDefaults={formDefaults}/>
  );
};

export const CompanyTransportServicesReportFormPage = (props: Props) => {
    const {formDefaults} = props;

    const navigate = useNavigate();
    const {t} = useTranslation(["common"]);
    const {regionId, appCategory, baseQueryApi} = useApp()

    const {scrollIntoView: scrollErrorIntoView, targetRef: errorTargetRef} = useScrollIntoView<HTMLDivElement>({
      offset: 120,
      duration: 0,
    });

    const {scrollIntoView, targetRef} = useScrollIntoView<HTMLDivElement>({
      offset: 120,
      duration: 0,
    });

    const {
      download,
      report,
      error,
      loading
    } = useDownloadCompanyTransportServicesReport({baseQueryApi})

    const onFormSubmit = useCallback((formValues: CompanyTransportServicesReportFormValues) => {

      const criteria: SearchCriteria = {
        fromDttm: adjustToTimeZone(formValues.fromDate).getTime(),
        toDttm: adjustToTimeZone(formValues.toDate).getTime(),
        regionId: regionId,
        transportCategory: formValues.category?.code,
        operatorIdList: formValues.operators?.map(getObjectId),
        personIdList: formValues.drivers?.map(getObjectId),
        startShiftReport: formValues.reportTimeBasis === "startShift",
        endShiftReport: formValues.reportTimeBasis === "endShift",
      }

      download(criteria)
    }, [regionId, download])

    useEffect(() => {
      if (report) {
        scrollIntoView({alignment: 'start'})
      }
    }, [report, scrollIntoView]);

    useEffect(() => {
      if (error) {
        scrollErrorIntoView({alignment: 'start'})
      }
    }, [error, scrollErrorIntoView]);

    return (
      <ThemedPage>
        {(classes) => (
          <Container className={classes.container} fluid={true} py="md">
            <Center>
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {t("common.page.report.companytransportservices.title", "Company Transport Services Report", {ns: "common"})}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                {error &&
                  <>
                    <Alert ref={errorTargetRef} icon={<IconAlertCircle size="1rem"/>} title="Error" color="red">
                      {error?.detail ?? t("common.error", "Error", {ns: "common"})}
                    </Alert>
                    <Space h="md"/>
                  </>
                }
                {report &&
                  <>
                    <Alert ref={targetRef} icon={<IconAlertCircle size="1rem"/>} title="Success" color="green">
                      <Anchor href={report} target={"_blank"}>
                        {t("common.action.download", "Download")}
                      </Anchor>
                    </Alert>
                    <Space h="md"/>
                  </>
                }
                <Box pos="relative">
                  <LoadingOverlay visible={loading}/>
                  <CompanyTransportServicesReportForm
                    defaultValues={{
                      ...(formDefaults ?? {}),
                      category: formDefaults?.category ?? appCategory
                    }}
                    options={{
                      regionId: regionId,
                      category: appCategory,
                    }}
                    submitting={loading}
                    onSubmit={onFormSubmit}/>
                </Box>
              </Paper>
            </Center>
          </Container>
        )}
      </ThemedPage>
    );
  }
;

